import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './ScrollBox.scss';

// Scrollable container with "jump to top / bottom" arrows pinned over the
// scrollbar (top-right / bottom-right), shown only while the content overflows.
// Pass `resizable` to let the user drag the bottom edge to resize the container.
export default function ScrollBox({
  className = '',
  wrapperClassName = '',
  wrapperStyle,
  resizable = false,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const [canScroll, setCanScroll] = useState(false);
  const [thumb, setThumb] = useState({ top: 0, height: 0 });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight > 1;
    setCanScroll(scrollable);

    const track = trackRef.current;
    if (!scrollable || !track) return;
    // custom thumb travels only within the track (already inset from the buttons),
    // so it always stops short of them regardless of native scrollbar rendering
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(24, trackHeight * (el.clientHeight / el.scrollHeight));
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const thumbTop = maxScrollTop > 0 ? (el.scrollTop / maxScrollTop) * (trackHeight - thumbHeight) : 0;
    // bail out on unchanged values so we don't return a new object every call,
    // which would otherwise re-render forever via useLayoutEffect(update) below
    setThumb((prev) =>
      Math.abs(prev.top - thumbTop) < 0.5 && Math.abs(prev.height - thumbHeight) < 0.5
        ? prev
        : { top: thumbTop, height: thumbHeight },
    );
  }, []);

  // Re-measure after every render so late/async content is caught.
  useLayoutEffect(update);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    const raf = requestAnimationFrame(update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    if (mo) mo.observe(el, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [update]);

  const jumpTo = (toBottom) => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: toBottom ? el.scrollHeight : 0, behavior: 'smooth' });
  };

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    const el = ref.current;
    const track = trackRef.current;
    if (!el || !track) return;
    draggingRef.current = true;
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const trackHeight = track.clientHeight;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const draggableTrack = trackHeight - thumb.height;

    const onMove = (moveEvent) => {
      if (!draggingRef.current || draggableTrack <= 0) return;
      const deltaScroll = ((moveEvent.clientY - startY) / draggableTrack) * maxScrollTop;
      el.scrollTop = Math.min(maxScrollTop, Math.max(0, startScrollTop + deltaScroll));
    };
    const onUp = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };


  return (
    <div
      className={`scroll-box ${wrapperClassName} ${resizable ? 'scroll-box--resizable' : ''}`}
      style={wrapperStyle}
    >
      {canScroll && (
        <button
          type="button"
          className="scroll-box__btn scroll-box__btn--up"
          onClick={() => jumpTo(false)}
          aria-label="Scroll to top"
        >
          <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M5 2L9 8H1z" fill="currentColor" />
          </svg>
        </button>
      )}
      {resizable ? (
        <div
          ref={ref}
          className={`scroll-box__content scroll-box__content--resizable ${className}`}
          onScroll={update}
          {...rest}
        >
          {children}
        </div>
      ) : (
        <div ref={ref} className={`scroll-box__content ${className}`} onScroll={update} {...rest}>
          {children}
        </div>
      )}
      {canScroll && (
        <button
          type="button"
          className="scroll-box__btn scroll-box__btn--down"
          onClick={() => jumpTo(true)}
          aria-label="Scroll to bottom"
        >
          <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M5 8L1 2H9z" fill="currentColor" />
          </svg>
        </button>
      )}
      {canScroll && (
        <div ref={trackRef} className="scroll-box__track">
          <div
            className="scroll-box__thumb"
            style={{ top: thumb.top, height: thumb.height }}
            onMouseDown={onThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
}
